var __defProp = Object.defineProperty;
var __name = (target, value) => __defProp(target, "name", { value, configurable: true });

// src/errors/api-client-error.ts
var ApiClientError = class extends Error {
  static {
    __name(this, "ApiClientError");
  }
};

// src/errors/no-response-error.ts
import _ from "lodash";

// src/helpers.ts
import { redactString } from "@repo/lib-utils";
function makeEndpointFromConfig({ method, url, baseURL }) {
  return `${method?.toUpperCase() ?? ""} ${baseURL ?? ""}${url ?? ""}`.trim();
}
__name(makeEndpointFromConfig, "makeEndpointFromConfig");
var VISIBLE_HEADER_CHARACTERS = {
  startVisibleLength: 4,
  endVisibleLength: 0
};
function redactHeaders(headers, headersToRedact) {
  if (headers === void 0) {
    return void 0;
  }
  const lowercaseHeadersToRedact = headersToRedact.map((header) => header.toLowerCase());
  return Object.fromEntries(Object.entries(headers).map(([name, value]) => [
    name,
    lowercaseHeadersToRedact.includes(name.toLowerCase()) ? redactString(String(value), VISIBLE_HEADER_CHARACTERS) : value
  ]));
}
__name(redactHeaders, "redactHeaders");

// src/errors/no-response-error.ts
var NoResponseError = class extends ApiClientError {
  static {
    __name(this, "NoResponseError");
  }
  request;
  durationMs;
  constructor(error, duration) {
    const endpoint = makeEndpointFromConfig(_.pick(error.config, "method", "url", "baseURL"));
    super(`Request to ${endpoint} was made but no response was received after ${duration}ms. ${error.message}`);
    this.durationMs = duration;
    this.request = {
      body: error.config?.data,
      baseUrl: error.config?.baseURL,
      method: error.config?.method,
      url: error.config?.url
    };
  }
};

// src/errors/request-error.ts
import _2 from "lodash";
var RequestError = class extends ApiClientError {
  static {
    __name(this, "RequestError");
  }
  request;
  constructor(error, duration, headersToRedact) {
    const endpoint = makeEndpointFromConfig(_2.pick(error.config, "method", "url", "baseURL"));
    super(`Something happened after ${duration}ms in setting up the request to ${endpoint} that triggered an error: ${error.message}`);
    this.request = {
      body: error.config?.data,
      headers: redactHeaders(error.config?.headers, headersToRedact),
      baseUrl: error.config?.baseURL,
      method: error.config?.method,
      url: error.config?.url
    };
  }
};

// src/errors/response-error.ts
import _3 from "lodash";
var RESPONSE_ERROR_KIND = "RESPONSE_ERROR";
var ResponseError = class extends ApiClientError {
  static {
    __name(this, "ResponseError");
  }
  kind = RESPONSE_ERROR_KIND;
  statusCode;
  data;
  request;
  durationMs;
  constructor(error, duration) {
    const endpoint = makeEndpointFromConfig(_3.pick(error.config, "method", "url", "baseURL"));
    const { status, data } = error.response;
    super(`Call to ${endpoint} failed (HTTP ${status}) after ${duration}ms. Response body: ${JSON.stringify(data)}`);
    this.statusCode = status;
    this.data = data;
    this.durationMs = duration;
    this.request = {
      body: error.config?.data,
      baseUrl: error.config?.baseURL,
      method: error.config?.method,
      url: error.config?.url
    };
  }
};
function isResponseError(error) {
  const candidate = error;
  return error instanceof Error && candidate?.kind === RESPONSE_ERROR_KIND && typeof candidate.statusCode === "number";
}
__name(isResponseError, "isResponseError");

// src/errors/response-status-error.ts
var ResponseStatusError = class extends ApiClientError {
  static {
    __name(this, "ResponseStatusError");
  }
  receivedStatus;
  constructor({ config, data }, expectedStatus, receivedStatus, duration) {
    const endpoint = makeEndpointFromConfig(config);
    const expectedStatusMessage = typeof expectedStatus === "number" ? expectedStatus : `${expectedStatus.join(" or ")}`;
    super(`Call to ${endpoint} return status ${receivedStatus} after ${duration}ms but ${expectedStatusMessage} was expected. Response body: ${JSON.stringify(data)}`), this.receivedStatus = receivedStatus;
  }
};

// src/errors/response-type-error.ts
var ResponseTypeError = class extends ApiClientError {
  static {
    __name(this, "ResponseTypeError");
  }
  constructor({ config, data }, error, duration) {
    const endpoint = makeEndpointFromConfig(config);
    super(`Call to ${endpoint} succeeded after ${duration}ms but received an unexpected response. Error: ${error.toString()} Response body: ${JSON.stringify(data)}`, {
      cause: error
    });
  }
};

// src/http-client.ts
import axios from "axios";
import axiosRetry from "axios-retry";
import { z } from "zod";
import { redactString as redactString2 } from "@repo/lib-utils";
var DEFAULT_TIMEOUT_IN_MS = 5e3;
var DEFAULT_REDACTION_CONFIG = {
  queryParams: [
    "apikey"
  ],
  headers: [
    "authorization"
  ]
};
var HttpClient = class {
  static {
    __name(this, "HttpClient");
  }
  axios;
  redactionConfig;
  constructor({ retryConfig: axiosRetryConfig, logger, redactionConfig, ...axiosConfig } = {}) {
    this.redactionConfig = {
      queryParams: [
        ...DEFAULT_REDACTION_CONFIG.queryParams,
        ...redactionConfig?.queryParams ?? []
      ],
      headers: [
        ...DEFAULT_REDACTION_CONFIG.headers,
        ...redactionConfig?.headers ?? []
      ]
    };
    this.axios = axios.create({
      timeout: DEFAULT_TIMEOUT_IN_MS,
      ...axiosConfig
    });
    if (logger !== void 0) {
      this.enableLogging(logger);
    }
    if (axiosRetryConfig !== void 0) {
      axiosRetry(this.axios, axiosRetryConfig);
    }
  }
  setAuthorization(token) {
    this.axios.defaults.headers.common.Authorization = `Bearer ${token}`;
    this.axios.defaults.headers.Authorization = `Bearer ${token}`;
  }
  setAuthorizationProvider(getToken) {
    this.axios.interceptors.request.use(async (request) => {
      const token = await getToken();
      if (token !== void 0) {
        request.headers.Authorization = `Bearer ${token}`;
      }
      return request;
    });
  }
  patch(url, options = {}) {
    return this.request({
      ...options,
      method: "patch",
      url
    });
  }
  post(url, options = {}) {
    return this.request({
      ...options,
      method: "post",
      url
    });
  }
  put(url, options = {}) {
    return this.request({
      ...options,
      method: "put",
      url
    });
  }
  get(url, options = {}) {
    return this.request({
      ...options,
      method: "get",
      url
    });
  }
  delete(url, options = {}) {
    return this.request({
      ...options,
      method: "delete",
      url
    });
  }
  async request({ expectedStatus, schema = z.unknown(), ...axiosConfig }) {
    const startDate = /* @__PURE__ */ new Date();
    try {
      const response = await this.axios.request(axiosConfig);
      if (typeof expectedStatus === "number" && expectedStatus !== response.status || Array.isArray(expectedStatus) && !expectedStatus.includes(response.status)) {
        throw new ResponseStatusError(response, expectedStatus, response.status, (/* @__PURE__ */ new Date()).getTime() - startDate.getTime());
      }
      const result = await schema.safeParseAsync(response.data);
      if (result.success === true || response.status === 204) {
        return result.data;
      }
      throw new ResponseTypeError(response, result.error, (/* @__PURE__ */ new Date()).getTime() - startDate.getTime());
    } catch (error) {
      if (axios.isAxiosError(error)) {
        throw this.handleAxiosError(error, (/* @__PURE__ */ new Date()).getTime() - startDate.getTime());
      }
      throw error;
    }
  }
  handleAxiosError(error, duration) {
    const { response, request } = error;
    if (response !== void 0) {
      return new ResponseError({
        ...error,
        response
      }, duration);
    }
    if (request !== void 0) {
      return new NoResponseError({
        ...error,
        request,
        response
      }, duration);
    }
    return new RequestError({
      ...error,
      request,
      response
    }, duration, this.redactionConfig.headers);
  }
  redactValues(record, keysToRedact) {
    if (record === void 0) {
      return void 0;
    }
    const lowercaseKeysToRedact = keysToRedact.map((key) => key.toLowerCase());
    return Object.fromEntries(Object.entries(record).map(([key, value]) => [
      key,
      lowercaseKeysToRedact.includes(key.toLowerCase()) && typeof value === "string" ? redactString2(value) : value
    ]));
  }
  enableLogging(logger) {
    this.axios.interceptors.request.use((request) => {
      logger.info(`Sending HTTP request ${request.method?.toUpperCase()} ${request.url ? request.url : request.baseURL}`, {
        baseUrl: request.baseURL,
        data: JSON.stringify(request.data),
        headers: JSON.stringify(redactHeaders(request.headers, this.redactionConfig.headers)),
        params: this.redactValues(request.params, this.redactionConfig.queryParams)
      });
      return request;
    });
    this.axios.interceptors.response.use((response) => {
      logger.info(`Received HTTP response ${response.status}`, {
        data: JSON.stringify(response.data),
        headers: JSON.stringify(redactHeaders(response.headers, this.redactionConfig.headers)),
        status: response.status
      });
      return response;
    });
  }
};
export {
  ApiClientError,
  HttpClient,
  NoResponseError,
  RequestError,
  ResponseError,
  ResponseStatusError,
  ResponseTypeError,
  isResponseError,
  makeEndpointFromConfig,
  redactHeaders
};
//# sourceMappingURL=index.js.map